import { useState, useMemo, useCallback } from 'react';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import { qNotesByEncounter, type PatientNoteRow } from '@helios/data-access';

// ─── Constants ───

const NOTE_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'clinical', label: 'Clinical' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'admin', label: 'Admin' },
] as const;

const VISIBILITY_OPTIONS = [
  { value: 'team', label: 'Team' },
  { value: 'private', label: 'Private' },
  { value: 'front_desk', label: 'Front Desk' },
] as const;

const ALERT_LEVELS = [
  { value: 'none', label: 'None' },
  { value: 'info', label: 'Info', color: '#1d4ed8', bg: '#dbeafe' },
  { value: 'warning', label: 'Warning', color: '#92400e', bg: '#fef3c7' },
  { value: 'critical', label: 'Critical', color: '#dc2626', bg: '#fee2e2' },
] as const;

// ─── Badge helpers ───

function NoteTypeBadge({ type }: { type: string | null }) {
  const label = NOTE_TYPES.find((t) => t.value === type)?.label ?? type ?? 'General';
  return (
    <span
      style={{
        fontSize: 10,
        padding: '1px 8px',
        borderRadius: 'var(--radius-xs)',
        background: 'var(--bg-surface-2, #f5f5f5)',
        color: 'var(--text-muted)',
        fontWeight: 'var(--font-weight-medium)',
      }}
    >
      {label}
    </span>
  );
}

function VisibilityBadge({ visibility }: { visibility: string | null }) {
  const isPrivate = visibility === 'private';
  return (
    <span
      style={{
        fontSize: 10,
        padding: '1px 8px',
        borderRadius: 'var(--radius-xs)',
        background: isPrivate ? '#fef3c7' : 'var(--bg-surface-2, #f5f5f5)',
        color: isPrivate ? '#92400e' : 'var(--text-muted)',
        fontWeight: 'var(--font-weight-medium)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
      }}
    >
      {isPrivate && <NavIcon name="lock" />}
      {VISIBILITY_OPTIONS.find((v) => v.value === visibility)?.label ?? visibility ?? 'Team'}
    </span>
  );
}

function AlertBadge({ level }: { level: string | null }) {
  if (!level || level === 'none') return null;
  const cfg = ALERT_LEVELS.find((a) => a.value === level);
  if (!cfg || !('color' in cfg)) return null;
  return (
    <span
      style={{
        fontSize: 10,
        padding: '1px 8px',
        borderRadius: 'var(--radius-xs)',
        background: cfg.bg,
        color: cfg.color,
        fontWeight: 'var(--font-weight-semi)',
      }}
    >
      {cfg.label}
    </span>
  );
}

// ─── Helpers ───

function formatNoteDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Props ───

interface EncounterNotesTabProps {
  patientId: string;
  encounterId: string;
  visitId?: string | null;
}

// ─── Component ───

export function EncounterNotesTab({ patientId, encounterId, visitId }: EncounterNotesTabProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const userName = user?.displayName ?? user?.email ?? '';

  const { data: noteRows = [] } = usePowerSyncQuery<PatientNoteRow>(qNotesByEncounter(encounterId));

  // Best-effort private filtering
  const visibleNotes = useMemo(
    () => noteRows.filter((n) => n.visibility !== 'private' || n.author_user_id === userId),
    [noteRows, userId],
  );

  // ── Composer state ──
  const [composerOpen, setComposerOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [visibility, setVisibility] = useState('team');
  const [alertLevel, setAlertLevel] = useState('none');
  const [saving, setSaving] = useState(false);

  const resetComposer = useCallback(() => {
    setTitle('');
    setBody('');
    setNoteType('general');
    setVisibility('team');
    setAlertLevel('none');
    setComposerOpen(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!db || !body.trim()) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_notes
          (id, org_id, clinic_id, patient_id, encounter_id, visit_id, title, body, note_type, visibility, alert_level, author_user_id, author_name, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          orgId,
          null,
          patientId,
          encounterId,
          visitId ?? null,
          title.trim() || 'Note',
          body.trim(),
          noteType,
          visibility,
          alertLevel,
          userId,
          userName,
          now,
          now,
        ],
      );
      resetComposer();
    } finally {
      setSaving(false);
    }
  }, [
    db,
    orgId,
    patientId,
    encounterId,
    visitId,
    title,
    body,
    noteType,
    visibility,
    alertLevel,
    userId,
    userName,
    resetComposer,
  ]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_notes WHERE id = ?`, [id]);
    },
    [db],
  );

  const selectStyle = {
    height: 32,
    padding: '0 8px',
    fontSize: 'var(--font-size-xs)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-surface)',
    color: 'var(--text)',
  };

  return (
    <div
      style={{
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Notes
          </h2>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            Clinical notes for this encounter
          </p>
        </div>
        {!composerOpen && (
          <button
            onClick={() => setComposerOpen(true)}
            style={{
              height: 36,
              padding: '0 16px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: primary,
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <NavIcon name="plus" /> Add Note
          </button>
        )}
      </div>

      {/* Composer */}
      {composerOpen && (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--sp-4)',
            background: 'var(--bg-surface)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          {/* Selectors row */}
          <div
            style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', alignItems: 'center' }}
          >
            <select
              value={noteType}
              onChange={(e) => setNoteType(e.target.value)}
              style={selectStyle}
            >
              {NOTE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              style={selectStyle}
            >
              {VISIBILITY_OPTIONS.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
            <select
              value={alertLevel}
              onChange={(e) => setAlertLevel(e.target.value)}
              style={selectStyle}
            >
              {ALERT_LEVELS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <input
            type="text"
            placeholder="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{
              height: 34,
              padding: '0 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              outline: 'none',
            }}
          />

          {/* Body */}
          <textarea
            placeholder="Write your note..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              resize: 'vertical',
              fontFamily: 'inherit',
              outline: 'none',
              lineHeight: 'var(--line-height-body)',
            }}
          />

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button
              onClick={resetComposer}
              style={{
                height: 32,
                padding: '0 14px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !body.trim()}
              style={{
                height: 32,
                padding: '0 14px',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: !body.trim() ? 'var(--text-disabled)' : primary,
                color: '#fff',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: !body.trim() ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving...' : 'Save Note'}
            </button>
          </div>
        </div>
      )}

      {/* Notes list */}
      {visibleNotes.length === 0 && !composerOpen ? (
        <div
          style={{
            textAlign: 'center',
            padding: 'var(--sp-8)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
            border: '2px dashed var(--border)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          No notes for this encounter yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {visibleNotes.map((note) => {
            const isWarningOrCritical =
              note.alert_level === 'warning' || note.alert_level === 'critical';
            const alertCfg = ALERT_LEVELS.find(
              (a) => a.value === note.alert_level && 'color' in a,
            ) as { color: string; bg: string } | undefined;
            return (
              <div
                key={note.id}
                style={{
                  border: isWarningOrCritical
                    ? `1px solid ${alertCfg?.color ?? 'var(--border)'}`
                    : '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--sp-4)',
                  background: isWarningOrCritical
                    ? (alertCfg?.bg ?? 'var(--bg-surface)')
                    : 'var(--bg-surface)',
                }}
              >
                {/* Header: badges + meta */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <NoteTypeBadge type={note.note_type} />
                    <VisibilityBadge visibility={note.visibility} />
                    <AlertBadge level={note.alert_level} />
                  </div>
                  {/* Delete button */}
                  {note.author_user_id === userId && (
                    <button
                      onClick={() => handleDelete(note.id)}
                      style={{
                        width: 24,
                        height: 24,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-placeholder)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <NavIcon name="x" />
                    </button>
                  )}
                </div>

                {/* Title */}
                {note.title && note.title !== 'Note' && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 'var(--font-weight-semi)',
                      color: 'var(--text)',
                      marginBottom: 4,
                    }}
                  >
                    {note.title}
                  </div>
                )}

                {/* Body */}
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text)',
                    lineHeight: 'var(--line-height-body)',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {note.body}
                </div>

                {/* Footer: author + timestamp */}
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--sp-3)',
                    marginTop: 8,
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {note.author_name && <span>{note.author_name}</span>}
                  <span>{formatNoteDate(note.created_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
